<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      style="background: rgba(0,0,0,0.2); backdrop-filter: blur(4px);"
      @click.self="$emit('close')"
    >
      <div class="bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-3xl flex flex-col overflow-hidden">

        <!-- Header -->
        <div class="flex items-center justify-between px-6 pt-5 pb-4 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0">
          <h2 class="text-base font-semibold text-zinc-900 dark:text-white">{{ modalTitle }}</h2>
          <button
            @click="$emit('close')"
            class="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Scrollable form body -->
        <div class="overflow-y-auto max-h-[68vh] px-6 py-5 space-y-3">

          <!-- framing label (required for alternatives, optional for the main entry) -->
          <div v-if="entryType !== 'skill'" class="form-group">
            <label class="form-label">
              Framing Label
              <span v-if="isVariant" class="text-red-400">*</span>
              <span v-else class="font-normal text-zinc-400">optional</span>
            </label>
            <input v-model="form.variant_label" class="form-input" placeholder="e.g. Data Science framing" />
          </div>

          <!-- ── EXPERIENCE ── -->
          <template v-if="entryType === 'experience'">
            <div class="form-grid">
              <div class="form-group">
                <label class="form-label">Job Title <span class="text-red-400">*</span></label>
                <input v-model="form.job_title" class="form-input" placeholder="Software Engineer" />
              </div>
              <div class="form-group">
                <label class="form-label">Company <span class="text-red-400">*</span></label>
                <input v-model="form.company" class="form-input" placeholder="Acme Corp" />
              </div>
              <div class="form-group">
                <label class="form-label">Location</label>
                <input v-model="form.location" class="form-input" placeholder="London, UK" />
              </div>
              <div class="form-group">
                <label class="form-label">Employment Type</label>
                <select v-model="form.employment_type" class="form-input">
                  <option value="">Select…</option>
                  <option v-for="o in ['Full-time','Part-time','Internship','Contract','Freelance','Other']" :key="o" :value="o">{{ o }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Company URL</label>
                <input v-model="form.company_url" class="form-input" placeholder="https://company.com" />
              </div>
              <div class="form-group">
                <label class="form-label">Company Description</label>
                <textarea v-model="form.company_description" class="form-textarea" rows="2" placeholder="Brief description of the company…" />
              </div>
            </div>
            <DateRangeFields :form="form" />
            <BulletPointsField :bullets="form.bullets" @update="form.bullets = $event" />
          </template>

          <!-- ── EDUCATION ── -->
          <template v-else-if="entryType === 'education'">
            <div class="form-grid">
              <div class="form-group md:col-span-2">
                <label class="form-label">Institution <span class="text-red-400">*</span></label>
                <input v-model="form.institution" class="form-input" placeholder="London School of Economics" />
              </div>
              <div class="form-group">
                <label class="form-label">Program Name</label>
                <input v-model="form.program_name" class="form-input" placeholder="MSc Finance" />
              </div>
              <div class="form-group">
                <label class="form-label">Program Type</label>
                <select v-model="form.program_type" class="form-input">
                  <option value="">Select…</option>
                  <option v-for="o in [`Bachelor's`,`Master's`,'PhD','MBA','Certificate','Diploma','Other']" :key="o" :value="o">{{ o }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Field of Study</label>
                <input v-model="form.field_of_study" class="form-input" placeholder="Computer Science" />
              </div>
              <div class="form-group">
                <label class="form-label">GPA</label>
                <input v-model="form.gpa" class="form-input" placeholder="3.9" />
              </div>
              <div class="form-group">
                <label class="form-label">Location</label>
                <input v-model="form.location" class="form-input" placeholder="London, UK" />
              </div>
            </div>
            <DateRangeFields :form="form" />
            <BulletPointsField :bullets="form.bullets" @update="form.bullets = $event" />
          </template>

          <!-- ── ACTIVITY ── -->
          <template v-else-if="entryType === 'activity'">
            <div class="form-grid">
              <div class="form-group">
                <label class="form-label">Organization <span class="text-red-400">*</span></label>
                <input v-model="form.organization" class="form-input" placeholder="Investment Society" />
              </div>
              <div class="form-group">
                <label class="form-label">Role</label>
                <input v-model="form.role" class="form-input" placeholder="President" />
              </div>
              <div class="form-group">
                <label class="form-label">Activity Type</label>
                <select v-model="form.activity_type" class="form-input">
                  <option value="">Select…</option>
                  <option v-for="o in ['Club','Sport','Volunteering','Society','Competition','Research','Other']" :key="o" :value="o">{{ o }}</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Location</label>
                <input v-model="form.location" class="form-input" placeholder="London, UK" />
              </div>
              <div class="form-group">
                <label class="form-label">URL</label>
                <input v-model="form.url" class="form-input" placeholder="https://…" />
              </div>
            </div>
            <DateRangeFields :form="form" />
            <BulletPointsField :bullets="form.bullets" @update="form.bullets = $event" />
          </template>

          <!-- ── SKILL ── -->
          <template v-else-if="entryType === 'skill'">
            <div class="form-group">
              <label class="form-label">Category Name <span class="text-red-400">*</span></label>
              <input v-model="form.category_name" class="form-input" placeholder="Programming Languages" />
            </div>
            <div class="form-group">
              <label class="form-label">Content</label>
              <textarea
                v-model="form.content"
                class="form-textarea"
                rows="3"
                placeholder="Python, SQL, R — or free text describing this skill area"
              />
            </div>
          </template>

        </div>

        <!-- Footer -->
        <div class="flex items-center justify-between px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 flex-shrink-0">
          <p v-if="apiError" class="text-xs text-red-500">{{ apiError }}</p>
          <span v-else />
          <div class="flex items-center gap-2">
            <button
              @click="$emit('close')"
              class="px-4 py-2 text-sm font-medium text-zinc-500 dark:text-zinc-400 border border-zinc-100 dark:border-zinc-700 rounded-lg hover:border-zinc-200 dark:hover:border-zinc-600 transition-colors"
            >Cancel</button>
            <button
              @click="submit"
              :disabled="isSaving"
              class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            >{{ isSaving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, computed, watch, defineComponent, h } from 'vue'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const TYPE_CONFIG = {
  experience: { endpoint: '/api/master_experience_entries', paramKey: 'master_experience_entry' },
  education:  { endpoint: '/api/master_education_entries',  paramKey: 'master_education_entry'  },
  activity:   { endpoint: '/api/master_activities',         paramKey: 'master_activity'          },
  skill:      { endpoint: '/api/master_skill_categories',   paramKey: 'master_skill_category'   }
}

const ENTRY_TYPE_LABELS = {
  experience: 'Experience',
  education:  'Education',
  activity:   'Activity',
  skill:      'Skill'
}

const DateRangeFields = defineComponent({
  name: 'DateRangeFields',
  props: { form: Object },
  setup(props) {
    return () => h('div', { class: 'space-y-2' }, [
      h('div', { class: 'flex items-end gap-4 flex-wrap' }, [
        h('div', { class: 'form-group' }, [
          h('label', { class: 'form-label' }, 'Start'),
          h('div', { class: 'flex gap-1.5' }, [
            h('select', {
              value: props.form.start_month,
              onInput: e => { props.form.start_month = e.target.value ? Number(e.target.value) : null },
              class: 'form-input w-24'
            }, [
              h('option', { value: '' }, 'Month'),
              ...MONTHS.map((m, i) => h('option', { value: i + 1 }, m))
            ]),
            h('input', {
              type: 'number',
              value: props.form.start_year,
              onInput: e => { props.form.start_year = e.target.value ? Number(e.target.value) : null },
              min: 1990, max: 2035,
              class: 'form-input w-20',
              placeholder: 'Year'
            })
          ])
        ]),
        !props.form.is_current
          ? h('div', { class: 'form-group' }, [
              h('label', { class: 'form-label' }, 'End'),
              h('div', { class: 'flex gap-1.5' }, [
                h('select', {
                  value: props.form.end_month,
                  onInput: e => { props.form.end_month = e.target.value ? Number(e.target.value) : null },
                  class: 'form-input w-24'
                }, [
                  h('option', { value: '' }, 'Month'),
                  ...MONTHS.map((m, i) => h('option', { value: i + 1 }, m))
                ]),
                h('input', {
                  type: 'number',
                  value: props.form.end_year,
                  onInput: e => { props.form.end_year = e.target.value ? Number(e.target.value) : null },
                  min: 1990, max: 2035,
                  class: 'form-input w-20',
                  placeholder: 'Year'
                })
              ])
            ])
          : null,
      ]),
      h('label', { class: 'flex items-center gap-2 cursor-pointer' }, [
        h('input', {
          type: 'checkbox',
          checked: props.form.is_current,
          onChange: e => { props.form.is_current = e.target.checked },
          class: 'w-3.5 h-3.5 accent-primary'
        }),
        h('span', { class: 'text-xs text-zinc-500 dark:text-zinc-400' }, 'Currently active')
      ])
    ])
  }
})

const BulletPointsField = defineComponent({
  name: 'BulletPointsField',
  props: { bullets: Array },
  emits: ['update'],
  setup(props, { emit }) {
    const add    = () => emit('update', [...props.bullets, ''])
    const remove = (i) => emit('update', props.bullets.filter((_, idx) => idx !== i))
    const update = (i, val) => {
      const next = [...props.bullets]
      next[i] = val
      emit('update', next)
    }
    return () => h('div', { class: 'space-y-1.5' }, [
      h('label', { class: 'form-label' }, 'Key Points'),
      ...(props.bullets || []).map((b, i) =>
        h('div', { key: i, class: 'flex gap-2 items-center' }, [
          h('input', {
            value: b,
            onInput: e => update(i, e.target.value),
            class: 'form-input flex-1',
            placeholder: `Point ${i + 1}`
          }),
          h('button', {
            type: 'button',
            onClick: () => remove(i),
            class: 'text-zinc-300 hover:text-red-400 transition-colors text-base leading-none flex-shrink-0 px-1'
          }, '×')
        ])
      ),
      h('button', {
        type: 'button',
        onClick: add,
        class: 'self-start text-xs text-zinc-400 hover:text-primary transition-colors mt-0.5'
      }, '+ Add point')
    ])
  }
})

function blankForm(entryType, source) {
  const s = source || {}
  const base = {
    variant_label:    s.variant_label    || '',
    start_month:      s.start_month      || null,
    start_year:       s.start_year       || null,
    end_month:        s.end_month        || null,
    end_year:         s.end_year         || null,
    is_current:       s.is_current       || false,
    bullets:          Array.isArray(s.base_bullet_points) ? [...s.base_bullet_points] : ['']
  }
  switch (entryType) {
    case 'experience':
      return { ...base,
        job_title:           s.job_title           || '',
        company:             s.company             || '',
        company_url:         s.company_url         || '',
        location:            s.location            || '',
        employment_type:     s.employment_type     || '',
        company_description: s.company_description || ''
      }
    case 'education':
      return { ...base,
        institution:    s.institution    || '',
        program_name:   s.program_name   || '',
        program_type:   s.program_type   || '',
        field_of_study: s.field_of_study || '',
        gpa:            s.gpa            || '',
        location:       s.location       || ''
      }
    case 'activity':
      return { ...base,
        organization:  s.organization  || '',
        role:          s.role          || '',
        location:      s.location      || '',
        activity_type: s.activity_type || '',
        url:           s.url           || ''
      }
    case 'skill':
      return {
        category_name: s.category_name || '',
        content:       s.content       || ''
      }
    default:
      return base
  }
}

function buildPayload(entryType, paramKey, form, isVariant) {
  const bullets = (form.bullets || []).filter(b => b?.trim())
  switch (entryType) {
    case 'experience':
      return {
        [paramKey]: {
          job_title: form.job_title, company: form.company,
          company_url: form.company_url, location: form.location,
          employment_type: form.employment_type,
          company_description: form.company_description,
          start_month: form.start_month, start_year: form.start_year,
          end_month: form.is_current ? null : form.end_month,
          end_year: form.is_current ? null : form.end_year,
          is_current: form.is_current,
          base_bullet_points: bullets,
          variant_label: form.variant_label
        }
      }
    case 'education':
      return {
        [paramKey]: {
          institution: form.institution, program_name: form.program_name,
          program_type: form.program_type, field_of_study: form.field_of_study,
          gpa: form.gpa, location: form.location,
          start_month: form.start_month, start_year: form.start_year,
          end_month: form.is_current ? null : form.end_month,
          end_year: form.is_current ? null : form.end_year,
          is_current: form.is_current,
          base_bullet_points: bullets,
          variant_label: form.variant_label
        }
      }
    case 'activity':
      return {
        [paramKey]: {
          organization: form.organization, role: form.role,
          location: form.location, activity_type: form.activity_type,
          url: form.url,
          start_month: form.start_month, start_year: form.start_year,
          end_month: form.is_current ? null : form.end_month,
          end_year: form.is_current ? null : form.end_year,
          is_current: form.is_current,
          base_bullet_points: bullets,
          variant_label: form.variant_label
        }
      }
    case 'skill':
      return {
        [paramKey]: {
          category_name: form.category_name,
          content: form.content
        }
      }
    default:
      return {}
  }
}

export default {
  name: 'ProfileEditModal',
  components: { DateRangeFields, BulletPointsField },
  props: {
    isOpen:      { type: Boolean, required: true },
    entryType:   { type: String,  required: true },
    entry:       { type: Object,  default: null  },
    parentEntry: { type: Object,  default: null  }
  },
  emits: ['close', 'saved'],
  setup(props, { emit }) {
    const isSaving = ref(false)
    const apiError = ref('')
    const form     = ref({})

    const isVariant = computed(() => !!props.parentEntry && !props.entry)
    const isEdit    = computed(() => !!props.entry)

    const modalTitle = computed(() => {
      const label = ENTRY_TYPE_LABELS[props.entryType] || props.entryType
      if (isVariant.value) return `Add Framing · ${label}`
      if (isEdit.value)    return `Edit ${label}`
      return `Add ${label}`
    })

    watch(() => props.isOpen, (open) => {
      if (!open) return
      apiError.value = ''
      const source = props.entry || (isVariant.value ? props.parentEntry : null)
      form.value = blankForm(props.entryType, source)
      if (isVariant.value) form.value.variant_label = ''
    }, { immediate: true })

    const csrf = () =>
      document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    async function submit() {
      apiError.value = ''
      isSaving.value = true
      try {
        const { endpoint, paramKey } = TYPE_CONFIG[props.entryType]
        const payload = buildPayload(props.entryType, paramKey, form.value, isVariant.value)

        let url, method
        if (isEdit.value) {
          url = `${endpoint}/${props.entry.id}`; method = 'PATCH'
        } else if (isVariant.value) {
          url = `${endpoint}/${props.parentEntry.id}/create_variant`; method = 'POST'
        } else {
          url = endpoint; method = 'POST'
        }

        const res = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrf()
          },
          body: JSON.stringify(payload)
        })

        const data = await res.json()
        if (!res.ok || !data.success) {
          apiError.value = (data.errors || [data.message]).filter(Boolean).join(', ') || 'Save failed'
          return
        }

        const saved =
          data.master_experience_entry ||
          data.master_education_entry  ||
          data.master_activity         ||
          data.master_skill_category

        emit('saved', saved)
        emit('close')
      } catch (e) {
        apiError.value = 'Network error — please try again'
      } finally {
        isSaving.value = false
      }
    }

    return { form, isSaving, apiError, isVariant, isEdit, modalTitle, submit }
  }
}
</script>
